import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { hrmsGetRequest, hrmsPostRequest, hrmsUpdateRequest, hrmsDeleteRequest } from "../services/index";

const API_URL = "/api/employee-salaries";

// Fetch Employee Salaries
export const fetchEmployeeSalaries = createAsyncThunk("employeeSalaries/fetch", async () => {
  const response = await hrmsGetRequest(API_URL);
  return response.data;
});

// Fetch Employee Salary by Teacher Email
export const fetchEmployeeSalaryByTeacherEmail = createAsyncThunk("employeeSalaries/fetchByTeacherEmail", async (teacherEmail) => {
  const response = await hrmsGetRequest(`${API_URL}/teacher/${teacherEmail}`);
  return response.data;
});

// Fetch Active Employee Salary
export const fetchActiveEmployeeSalary = createAsyncThunk("employeeSalaries/fetchActive", async (teacherEmail) => {
  const response = await hrmsGetRequest(`${API_URL}/teacher/${teacherEmail}/active`);
  return response.data;
});

// Fetch Employee Salary History
export const fetchEmployeeSalaryHistory = createAsyncThunk("employeeSalaries/fetchHistory", async (teacherEmail) => {
  const response = await hrmsGetRequest(`${API_URL}/teacher/${teacherEmail}/history`);
  return response.data;
});

// Add Employee Salary
export const addEmployeeSalary = createAsyncThunk("employeeSalaries/add", async (salaryData) => {
  const response = await hrmsPostRequest(API_URL, salaryData);
  return response.data;
});

// Update Employee Salary
export const updateEmployeeSalary = createAsyncThunk("employeeSalaries/update", async ({ id, updatedData }) => {
  const response = await hrmsUpdateRequest(`${API_URL}/${id}`, updatedData);
  return response.data;
});

// Update Employee Salary by Teacher Email
export const updateEmployeeSalaryByTeacherEmail = createAsyncThunk("employeeSalaries/updateByTeacherEmail", async ({ teacherEmail, id, updatedData }) => {
  const response = await hrmsUpdateRequest(`${API_URL}/teacher/${teacherEmail}/${id}`, updatedData);
  return response.data;
});

// Delete Employee Salary
export const deleteEmployeeSalary = createAsyncThunk("employeeSalaries/delete", async (id) => {
  await hrmsDeleteRequest(`${API_URL}/${id}`);
  return id;
});

// Delete Employee Salary by Teacher Email
export const deleteEmployeeSalaryByTeacherEmail = createAsyncThunk("employeeSalaries/deleteByTeacherEmail", async ({ teacherEmail, id }) => {
  await hrmsDeleteRequest(`${API_URL}/teacher/${teacherEmail}/${id}`);
  return { teacherEmail, id };
});

const employeeSalarySlice = createSlice({
  name: "employeeSalaries",
  initialState: {
    employeeSalaries: [],
    activeSalary: null,
    salaryHistory: [],
    status: "idle",
    error: null,
  },
  reducers: {
    clearActiveSalary: (state) => {
      state.activeSalary = null;
    },
    clearSalaryHistory: (state) => {
      state.salaryHistory = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Employee Salaries
      .addCase(fetchEmployeeSalaries.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchEmployeeSalaries.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.employeeSalaries = action.payload;
      })
      .addCase(fetchEmployeeSalaries.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      
      // Fetch by Teacher Email
      .addCase(fetchEmployeeSalaryByTeacherEmail.fulfilled, (state, action) => {
        state.employeeSalaries = action.payload;
      })
      
      // Fetch Active Salary
      .addCase(fetchActiveEmployeeSalary.fulfilled, (state, action) => {
        state.activeSalary = action.payload;
      })
      
      // Fetch Salary History
      .addCase(fetchEmployeeSalaryHistory.fulfilled, (state, action) => {
        state.salaryHistory = action.payload;
      })
      
      // Add Employee Salary
      .addCase(addEmployeeSalary.fulfilled, (state, action) => {
        state.employeeSalaries.push(action.payload);
      })
      
      // Update Employee Salary
      .addCase(updateEmployeeSalary.fulfilled, (state, action) => {
        const index = state.employeeSalaries.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.employeeSalaries[index] = action.payload;
        }
      })
      
      // Update by Teacher Email
      .addCase(updateEmployeeSalaryByTeacherEmail.fulfilled, (state, action) => {
        const index = state.employeeSalaries.findIndex((s) => s.id === action.payload.id);
        if (index !== -1) {
          state.employeeSalaries[index] = action.payload;
        }
      })
      
      // Delete Employee Salary
      .addCase(deleteEmployeeSalary.fulfilled, (state, action) => {
        state.employeeSalaries = state.employeeSalaries.filter((s) => s.id !== action.payload);
      })
      
      // Delete by Teacher Email
      .addCase(deleteEmployeeSalaryByTeacherEmail.fulfilled, (state, action) => {
        state.employeeSalaries = state.employeeSalaries.filter((s) => s.id !== action.payload.id);
      });
  },
});

export const { clearActiveSalary, clearSalaryHistory } = employeeSalarySlice.actions;

export default employeeSalarySlice.reducer; 